import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSElement,
  WSText
} from "@wingspanhq/fe-component-library";
import { Engagement } from "@wingspanhq/wingspan-api-sdk";
import { ErrorAlert } from "@components/ErrorAlert";
import React from "react";
import { ActionEngagementDetails } from "../components/ActionEngagementDetails";
import { queriesEngagements } from "@query";

export interface RestoreEngagementTypeModalProps {
  engagement: Engagement;
  onClose: () => void;
}

export const RestoreEngagementTypeModal: React.FC<
  RestoreEngagementTypeModalProps
> = ({ engagement, onClose }) => {
  const { openSnackbar } = useWSSnackbar();
  const [restoreEngagementType, updateEngagementTypeMeta] =
    queriesEngagements.restoreEngagement.useMutation();

  const handleRestore = async () => {
    await restoreEngagementType({
      engagementId: engagement.id!
    });

    openSnackbar({
      theme: "dark",
      type: "success",
      message: "Engagement restored"
    });
    onClose();
  };

  return (
    <>
      <WSText.ParagraphSm mb="L" weight="book">
        Are you sure you wish to restore this engagement?
      </WSText.ParagraphSm>
      <ActionEngagementDetails engagement={engagement} />

      <WSAlert
        my="2XL"
        theme="info"
        icon="info-circle"
        size="M"
        title="Restoring engagement - what to expect:"
      >
        <WSElement as="ul" ml="L">
          <li>
            Any archived assignments associated with this engagement will remain
            archived and can be restored individually.
          </li>
          <li>Changes can be made to this engagement.</li>
          <li>Invoices can be created for this engagement.</li>
        </WSElement>
      </WSAlert>

      <ErrorAlert
        error={updateEngagementTypeMeta.error}
        mb="XL"
        context="Engagement"
      />

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Secondary"
          },
          {
            label: "Restore engagement",
            loading: updateEngagementTypeMeta.isLoading,
            onAsyncClick: handleRestore,
            kind: "Primary"
          }
        ]}
      />
    </>
  );
};

export const useRestoreEngagementTypeModal = () => {
  return useWSModal(RestoreEngagementTypeModal, {
    title: "Restore engagement",
    size: "M"
  });
};
